import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  getMyInsurance,
  submitInsurance,
  getInsuranceDownloadUrl,
} from "@/lib/insurance.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/bartender/insurance")({
  head: () => ({ meta: [{ title: "Insurance — The Hospitality Reserve" }] }),
  component: InsurancePage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">Error: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

function InsurancePage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["insurance", "mine"], queryFn: () => getMyInsurance() });
  const ins = data?.insurance ?? null;

  const [provider, setProvider] = useState("");
  const [policy, setPolicy] = useState("");
  const [expires, setExpires] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const mut = useMutation({
    mutationFn: submitInsurance,
    onSuccess: (res) => {
      if ("error" in res) {
        toast.error(res.error);
      } else {
        toast.success("Insurance submitted for review.");
        qc.invalidateQueries({ queryKey: ["insurance", "mine"] });
      }
    },
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { toast.error("Please attach your insurance certificate."); return; }
    setUploading(true);
    try {
      const { data: { session: __s } } = await supabase.auth.getSession(); const user = { user: __s?.user ?? null };
      if (!user.user) throw new Error("Not signed in");
      const ext = file.name.split(".").pop() || "pdf";
      const path = `${user.user.id}/policy-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("insurance-documents")
        .upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
      if (error) throw error;
      await mut.mutateAsync({ data: {
        provider, policy_number: policy, expires_at: expires, document_path: path,
      } });
      setFile(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function openDoc(path: string) {
    const res = await getInsuranceDownloadUrl({ data: { path } });
    if ("error" in res) { toast.error(res.error); return; }
    window.open(res.url, "_blank");
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Compliance</p>
        <h1 className="text-3xl font-light">Public liability insurance</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          As a self-employed contractor, you are required to hold valid public liability insurance.
          Upload your certificate of insurance and we will keep it on file until expiry.
        </p>

        {ins && (
          <div className="mt-8 border border-border p-6 text-sm">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">Current</p>
              <span className={`text-[11px] uppercase tracking-wide-luxury ${
                ins.insurance_status === "verified" ? "text-emerald-500"
                : ins.insurance_status === "rejected" ? "text-destructive"
                : ins.insurance_status === "submitted" ? "text-amber-500" : "text-muted-foreground"
              }`}>{(ins.insurance_status as string) ?? "not submitted"}</span>
            </div>
            {ins.insurance_provider && (
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <dt className="text-muted-foreground">Provider</dt><dd>{ins.insurance_provider}</dd>
                <dt className="text-muted-foreground">Policy number</dt><dd className="font-mono text-xs">{ins.insurance_policy_number}</dd>
                <dt className="text-muted-foreground">Expires</dt><dd>{ins.insurance_expires_at}</dd>
              </dl>
            )}
            {ins.insurance_notes && (
              <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                Admin note: {ins.insurance_notes}
              </p>
            )}
            {ins.insurance_document_path && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => openDoc(ins.insurance_document_path as string)}>
                View uploaded certificate
              </Button>
            )}
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 space-y-4 border border-border p-6">
          <p className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
            {ins?.insurance_provider ? "Update / renew" : "Submit for review"}
          </p>
          <div>
            <Label>Insurer</Label>
            <Input required value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="e.g. Hiscox" />
          </div>
          <div>
            <Label>Policy number</Label>
            <Input required value={policy} onChange={(e) => setPolicy(e.target.value)} />
          </div>
          <div>
            <Label>Expiry date</Label>
            <Input required type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
          <div>
            <Label>Certificate (PDF or image)</Label>
            <Input required type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <Button type="submit" disabled={uploading || mut.isPending}>
            {uploading || mut.isPending ? "Uploading…" : "Submit for review"}
          </Button>
        </form>
      </main>
    </div>
  );
}
